import {PIXI} from '@colonist/pixi'
import {SelectCardsToGiveToAnotherPlayer} from '@colonist/game-card-types'
import {UIGamePlayerAvatarView} from '../ui_game_players/UIGamePlayerAvatarView'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameCardContainerView} from '../ui_game_card/UIGameCardContainerView'
import {UIGameManager} from '../UIGameManager'
import {UIGamePopupConfirmationButtons} from './UIGamePopupConfirmationButtons'
import {UIGameCanvasController} from '../UIGameCanvasController'
import {ArrayUtils} from '@colonist/utils'
import {CardEnum, PlayerColor} from '@colonist/game-states'

export abstract class UIGamePopupSelectCardToGiveToAnotherPlayer extends SectionBgAdaptiveView {
    protected readonly uiGameManager: UIGameManager
    protected readonly popupImageAndDescription: PIXI.Container
    protected readonly selectCardFormat: SelectCardsToGiveToAnotherPlayer
    protected selectedCardContainer: UIGameCardContainerView
    protected victimAvatarView: UIGamePlayerAvatarView | undefined
    protected confirmationButtons: UIGamePopupConfirmationButtons
    protected playerCardContainer: UIGameCardContainerView
    protected readonly canvas: UIGameCanvasController

    constructor(uiGameManager: UIGameManager, popupImageAndDescription: PIXI.Container, selectCardFormat: SelectCardsToGiveToAnotherPlayer) {
        super()
        this.uiGameManager = uiGameManager
        this.popupImageAndDescription = popupImageAndDescription
        this.selectCardFormat = selectCardFormat

        this.canvas = this.uiGameManager.canvasController
        this.canvas.app.stage.addChild(this)

        this.popupImageAndDescription = popupImageAndDescription
        this.addChild(this.popupImageAndDescription)

        this.createSelectedCardContainer()
        this.addVictimAvatarView(this.selectCardFormat.playerReceivingCard)
        this.createConfirmationButtons()
        this.buildContainer()
        this.setContainerY()
        this.createPlayerCardContainer()
        this.setChangeCardActions()
        this.updatePlayerCards()
    }

    protected abstract createSelectedCardContainer()

    private setChangeCardActions() {
        const cardChangeAction = () => this.updateCheckMark()
        this.selectedCardContainer.setCardAddAction(cardChangeAction)
        this.selectedCardContainer.setCardRemoveAction(cardChangeAction)

        this.selectedCardContainer.setCardClickAction((cardEnum: CardEnum) => {
            this.selectedCardContainer.removeCard(cardEnum)
            this.playerCardContainer.addNewCardOfType(cardEnum)
            this.updateCheckMark()
        })

        this.playerCardContainer.setCardClickAction((cardEnum: CardEnum) => this.addCard(cardEnum))
    }

    protected abstract createConfirmationButtons()

    private buildContainer(horizontalBuffer: number = 0, verticalBuffer: number = 0) {
        this.drawTextureBgAroundContent(horizontalBuffer, verticalBuffer)
        this.confirmationButtons.y = this.absoluteHeight - this.confirmationButtons.height
    }

    protected abstract setContainerY()

    protected abstract createPlayerCardContainer()

    private addVictimAvatarView(victim: PlayerColor) {
        const padding = this.canvas.itemSize * 0.1
        const victimAvatarSize = this.selectedCardContainer.height
        const playerState = this.uiGameManager.gameController.getPlayerState(victim)
        const victimAvatarX = this.selectedCardContainer.width - victimAvatarSize - padding
        const victimAvatarY = this.uiGameManager.canvasController.margin
        this.victimAvatarView = new UIGamePlayerAvatarView(victimAvatarX, victimAvatarY, victimAvatarSize, this.selectedCardContainer, playerState, true)
    }

    private hasEnoughCards(): boolean {
        return this.selectedCardContainer.cardInventory.cardViews.length == this.selectCardFormat.selectCardFormat.amountOfCardsToSelect
    }

    private updateCheckMark() {
        this.confirmationButtons.checkButton.inactivateOpacity()
        if(!this.hasEnoughCards()) return

        this.confirmationButtons.checkButton.activateOpacity()
    }

    private addCard(cardEnum: CardEnum) {
        if(!ArrayUtils.elementInArray(this.selectCardFormat.selectCardFormat.validCardsToSelect, cardEnum)) return
        if(this.selectCardFormat.selectCardFormat.amountOfCardsToSelect == 1) this.resetCardSelection()
        if(this.hasEnoughCards()) return
        if(!this.canPlayerAddCard(cardEnum)) return

        this.selectedCardContainer.addNewCardOfType(cardEnum)
        this.playerCardContainer.removeCard(cardEnum)
    }

    private canPlayerAddCard(cardEnum: CardEnum): boolean {
        const currentSelectedAmount = this.selectedCardContainer.cardInventory.getCardTypeCount(cardEnum)
        const currentResourceCardAmount = this.uiGameManager.gameController.totalResourceCardOfType(this.uiGameManager.gameController.myColor, cardEnum)
        if(currentResourceCardAmount > currentSelectedAmount) return true

        return false
    }

    private resetCardSelection() {
        this.selectedCardContainer.removeAllCards()
        this.playerCardContainer.removeAllCards()
        this.updatePlayerCards()
    }

    private updatePlayerCards() {
        const allCardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor)
        for(const card of allCardsInHand) {
            this.playerCardContainer.addNewCardOfType(card, false)
        }
        this.playerCardContainer.fadeInAllCardsExcludingCardsOfType(this.selectCardFormat.selectCardFormat.validCardsToSelect)
    }

    protected checkAction() {
        if(!this.hasEnoughCards()) return
        const selectedCards = this.selectedCardContainer.cardInventory.getAllCardEnums()
        this.uiGameManager.socketGameSend.selectCards(selectedCards)
        this.closeUI()
    }

    closeUI() {
        this.playerCardContainer.closeUI()
        if(this.parent == undefined) return
        this.parent.removeChild(this)
    }
}
